import React, { createContext, useContext } from "react";
import { parseEventMetadataAttr } from "./parseEventMetadataAttr";
import type { JSON, JSONObject } from "./types";

export type { JSONObject };

/**
 * Context for product event metadata that can be inherited by child components.
 * Used by useEventLogger hook to auto-merge metadata when logging events manually.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exports context alongside component
export const EventMetadataContext = createContext<JSONObject>({});

/**
 * Base props for EventMetadata - allows any JSON-serializable metadata properties
 * This is handy because you can write the metadata as attributes, but it means
 * that you could also be including React elements or other non-JSON values.
 * We could try to improve types here
 */
type EventMetadataBaseProps = {
  className?: string;
  [key: string]: JSON | React.ReactElement | React.ReactNode;
};

/** Props for wrapper mode (default) - renders a wrapper element around children */
type EventMetadataWrapperProps = EventMetadataBaseProps & {
  /** When false or omitted, renders a wrapper element */
  asChild?: false;
  /** HTML element to use as wrapper (default: "div") */
  as?: keyof JSX.IntrinsicElements;
  children?: React.ReactNode;
};

/** Props for attach mode - attaches metadata directly to the single child element */
type EventMetadataAttachProps = EventMetadataBaseProps & {
  /** When true, attaches data-event-metadata directly to the child element */
  asChild: true;
  /** Not allowed in attach mode */
  as?: never;
  /** Must be a single React element when asChild is true */
  children: React.ReactElement;
};

type EventMetadataProps = EventMetadataWrapperProps | EventMetadataAttachProps;

/**
 * Provides event metadata to child components via both:
 * 1. DOM attribute (`data-event-metadata`) - for automatic click event logging
 * 2. React Context - for manual event logging via `useEventLogger` hook
 *
 * Use this component instead of adding `data-event-metadata` directly to DOM elements
 * to ensure metadata is available to both automatic and manual event logging.
 *
 * @example Default wrapper mode (renders a div)
 * <EventMetadata districtId={districtId} featureArea="announcements">
 *   <MyComponent />
 * </EventMetadata>
 *
 * @example Custom wrapper element with `as` prop
 * <EventMetadata as="li" districtId={districtId}>
 *   <MyComponent />
 * </EventMetadata>
 *
 * @example Attach to children mode (no wrapper element)
 * <EventMetadata asChild districtId={districtId}>
 *   <button>Click me</button>
 * </EventMetadata>
 */
export function EventMetadata({ children, className, asChild, as, ...props }: EventMetadataProps) {
  const parentMetadata = useContext(EventMetadataContext);

  const mergedMetadata = { ...parentMetadata, ...props } as JSONObject;

  let content: React.ReactNode;

  if (asChild) {
    const child = children as React.ReactElement;
    const existingMetadata = parseEventMetadataAttr(child.props["data-event-metadata"]);

    content = React.cloneElement(child, {
      "data-event-metadata": JSON.stringify({ ...props, ...existingMetadata }),
    });
  } else {
    const Wrapper = as ?? "div";
    content = (
      <Wrapper className={className} data-event-metadata={JSON.stringify(props)}>
        {children}
      </Wrapper>
    );
  }

  return <EventMetadataContext.Provider value={mergedMetadata}>{content}</EventMetadataContext.Provider>;
}
